import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Instagram, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { TALENT_ROSTER } from '../data/companyData';

const TalentPage = () => {
  const { language } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedTalent, setSelectedTalent] = useState(null);

  const filters = [
    { key: 'all', label: { es: 'Todos', en: 'All' } },
    { key: 'artist', label: { es: 'Artistas', en: 'Artists' } },
    { key: 'athlete', label: { es: 'Deportistas', en: 'Athletes' } },
    { key: 'influencer', label: { es: 'Influencers', en: 'Influencers' } }
  ];

  const filteredTalent = activeFilter === 'all' 
    ? TALENT_ROSTER 
    : TALENT_ROSTER.filter(t => t.category === activeFilter);

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#D91616] mb-4 block">
              {language === 'es' ? 'Nuestro Roster' : 'Our Roster'}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white mb-6">
              {language === 'es' ? 'TALENTO DE CLASE MUNDIAL' : 'WORLD-CLASS TALENT'}
            </h1>
            <p className="text-xl text-zinc-400">
              {language === 'es'
                ? 'Representamos a los artistas, deportistas e influencers más destacados de México y Latinoamérica.'
                : 'We represent the most outstanding artists, athletes, and influencers from Mexico and Latin America.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-[#0A0A0A] sticky top-20 z-40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-4">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-2 text-sm font-semibold uppercase tracking-wider transition-all ${
                  activeFilter === filter.key
                    ? 'bg-[#D91616] text-white'
                    : 'bg-transparent border border-white/20 text-zinc-400 hover:border-[#D91616] hover:text-white'
                }`}
              >
                {filter.label[language]}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Talent Grid */}
      <section className="py-16 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            <AnimatePresence>
              {filteredTalent.map((talent, index) => (
                <motion.div
                  key={talent.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => setSelectedTalent(talent)}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden bg-[#0A0A0A]">
                    <div className="aspect-[3/4] overflow-hidden">
                      <img
                        src={talent.image_url}
                        alt={talent.name}
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                    </div>
                    
                    {/* Category Badge */}
                    <div className="absolute top-4 left-4">
                      <span className="bg-[#D91616] text-white text-xs font-bold uppercase tracking-wider px-3 py-1">
                        {talent.specialty}
                      </span>
                    </div>

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="text-xl font-bold text-white mb-1">{talent.name}</h3>
                      <p className="text-sm text-zinc-400 mb-2 line-clamp-2">{talent.highlights[language]}</p>
                      <div className="flex items-center gap-2 text-zinc-500 text-xs">
                        <Instagram className="w-3 h-3" />
                        <span>{talent.social.instagram}</span>
                        <span>•</span>
                        <span>{talent.social.followers}</span>
                      </div>
                    </div>

                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-[#D91616]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="bg-white text-[#050505] px-6 py-2 font-bold uppercase tracking-wider text-sm">
                        {language === 'es' ? 'Ver Perfil' : 'View Profile'}
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Talent Modal */}
      <AnimatePresence>
        {selectedTalent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTalent(null)}
            className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0A0A0A] max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="grid md:grid-cols-2">
                <div className="aspect-[3/4] md:aspect-auto">
                  <img
                    src={selectedTalent.image_url}
                    alt={selectedTalent.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8">
                  <span className="text-[#D91616] text-sm font-bold uppercase tracking-wider">
                    {selectedTalent.specialty}
                  </span>
                  <h2 className="font-outfit text-3xl font-bold text-white mt-2 mb-4">{selectedTalent.name}</h2>
                  <p className="text-zinc-400 mb-6">{selectedTalent.highlights[language]}</p>
                  
                  <h4 className="text-white font-semibold mb-3">{language === 'es' ? 'Logros Destacados' : 'Key Achievements'}</h4>
                  <ul className="space-y-2 mb-6">
                    {selectedTalent.achievements[language].map((achievement, i) => (
                      <li key={i} className="flex items-start gap-2 text-zinc-400 text-sm">
                        <div className="w-1.5 h-1.5 bg-[#D91616] mt-2 flex-shrink-0" />
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center gap-4 text-zinc-500 text-sm mb-6">
                    <Instagram className="w-4 h-4" />
                    <span>{selectedTalent.social.instagram}</span>
                    <span className="text-[#D91616]">{selectedTalent.social.followers} followers</span>
                  </div>

                  <button
                    onClick={() => setSelectedTalent(null)}
                    className="bg-[#D91616] text-white px-6 py-3 font-semibold uppercase tracking-wider hover:bg-[#ED1C24] transition-colors w-full"
                  >
                    {language === 'es' ? 'Cerrar' : 'Close'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA */}
      <section className="py-24 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-6">
            {language === 'es' ? '¿QUIERES SER PARTE DE NUESTRO ROSTER?' : 'WANT TO BE PART OF OUR ROSTER?'}
          </h2>
          <p className="text-zinc-400 mb-8">
            {language === 'es'
              ? 'Si crees que tienes lo que se necesita para ser representado por MERAKI, queremos conocerte.'
              : 'If you believe you have what it takes to be represented by MERAKI, we want to meet you.'}
          </p>
          <Link
            to={language === 'es' ? '/contacto' : '/contact'}
            className="inline-flex items-center gap-3 bg-[#D91616] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#ED1C24] transition-all"
          >
            {language === 'es' ? 'Aplica Ahora' : 'Apply Now'}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default TalentPage;
